import React from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, CheckCircle2, ArrowRight, ChevronDown } from "lucide-react";
import { CITIES, SERVICES, REVIEWS, PROJECTS, BUSINESS, IMAGES } from "@/data/content";
import { DEEP_SERVICES } from "@/data/deepServices";
import { SectionHeading, CTA, FinalCTA, ReviewCard } from "@/components/Shared";
import { SEO, breadcrumbSchema, faqSchema } from "@/components/SEO";
import { HScroll, HScrollItem, RoofAccent, DarkHeroAccent } from "@/components/RoofAccents";

// Generate unique per-city FAQs (no find-and-replace template)
const cityFaqs = (city) => [
  { q: `How much does roof replacement cost in ${city.name}, MI?`, a: `Most asphalt shingle roof replacements for homes in ${city.name} range from $8,500 to $18,000. Final cost depends on roof pitch, square footage, number of layers to tear off, and shingle grade. We give written, line-item estimates so you see exactly where the money goes.` },
  { q: `Do you handle insurance claims in ${city.name}?`, a: `Yes. We document storm damage with photos, meet your insurance adjuster on-site in ${city.name}, and file supplements when the first scope is short. We've handled claims with every major Michigan insurer.` },
  { q: `How fast can you respond to a leak in ${city.name}?`, a: `We offer 24-hour emergency tarping across ${city.name} and all of Oakland County. Call (248) 804-6933 and we'll get a tarp up before more water enters your home.` },
  { q: `Do you pull roofing permits in ${city.name}?`, a: `Yes. Most municipalities in Oakland County - including ${city.name} - require a roofing permit. We handle the permit-pulling and inspection scheduling as part of every replacement job.` },
];

// Optional per-city intro paragraph to avoid templated sameness
const cityIntro = {
  "waterford": "As a Waterford Township-based company, we service our own backyard first. We know the lakefront ice-dam patterns along Lotus Lake, Williams Lake and Elizabeth Lake, and we know how Waterford roofs take a beating from big-lake winds.",
  "clarkston": "Clarkston homes range from 1850s downtown historics to new subdivisions off Waldon and Sashabaw. We've worked on cedar shake restorations in the village and premium asphalt replacements on modern hip-roofs alike.",
  "white-lake": "White Lake roofs face constant wind exposure across the water. We default to 6-nail high-wind shingle fastening and upgraded ice & water shield for every replacement here - it's not an upsell, it's the right spec for the neighborhood.",
  "commerce": "Commerce Township homes often have complex multi-gabled architecture. Our crews are trained on hip valleys, dormer flashing, and skylight re-integration that keeps these rooflines watertight for decades.",
  "west-bloomfield": "West Bloomfield homeowners expect premium materials and quiet, clean job sites. We carry GAF Master Elite and CertainTeed SELECT certifications for this reason - your manufacturer warranty is backed at the highest tier.",
  "bloomfield-hills": "Estate properties in Bloomfield Hills demand craftsmanship and discretion. We protect landscaping with tarps, stage materials away from driveways, and keep job-site noise to Oakland County permit hours.",
  "rochester-hills": "Rochester Hills neighborhoods are family-focused and schedule-sensitive. Most of our full roof replacements here finish in 1-2 days with a magnetic nail sweep and final walkthrough before we leave.",
  "troy": "From Somerset Collection estates to the Big Beaver corridor subdivisions, we know Troy's building stock. We handle every Troy HOA's approval packet and pull all permits through the Troy Building Department.",
  "auburn-hills": "Auburn Hills combines newer subdivisions near Oakland University with older stock. We handle architectural rooflines typical of the 1990s-2010s builds here, including prominent hip-and-valley roofs that need precise flashing.",
  "birmingham": "Birmingham's walkable downtown neighborhoods and historic stock often call for heritage materials - cedar shake, slate, or architectural asphalt with hand-tuned ridge caps. We install to match the character of the street.",
  "pontiac": "Pontiac's housing mix - from historic bungalows on Oakland Avenue to newer builds near M-59 - means every roof is different. We offer honest, affordable options with the same warranty and crew as any other zip code.",
  "royal-oak": "Royal Oak's Woodward corridor and Main Street neighborhoods blend 1920s-40s bungalows with modern renovations. We handle Royal Oak's tight lots, close neighbors, and historic overlay approvals - and we match original roof details on character homes.",
  "lake-orion": "Lake Orion properties face serious four-season exposure: summer storms off the lake, winter ice buildup, and high wind loads on waterfront lots. We spec 130-mph architectural shingles, upgraded ice & water shield, and proper ridge venting as our baseline here.",
};

// Neighborhoods + nearby areas per city - drives unique geo-relevant content
const cityNeighborhoods = {
  "waterford": ["Lotus Lake", "Williams Lake", "Elizabeth Lake", "Cass Lake", "Loon Lake", "Drayton Plains", "Pontiac Lake"],
  "clarkston": ["Downtown Clarkston Village", "Waldon Woods", "Sashabaw Plains", "Deer Lake", "Independence Township", "Springfield Township"],
  "white-lake": ["Cedar Island Lake", "Pontiac Lake area", "M-59 corridor", "White Lake Township", "Highland Township"],
  "commerce": ["Union Lake", "Walled Lake area", "Commerce Lake", "Wolverine Lake", "Mariner Pointe"],
  "west-bloomfield": ["Walnut Lake", "Pine Lake", "Orchard Lake", "Maple Road corridor", "Long Lake area"],
  "bloomfield-hills": ["Kirk in the Hills", "Cranbrook", "Gilbert Lake", "Vhay Lake", "Square Lake corridor"],
  "rochester-hills": ["Stoney Creek", "Avon Hills", "Oakland Township border", "Auburn Road area", "Rochester border", "Tienken Road corridor"],
  "troy": ["Somerset Collection area", "Big Beaver corridor", "Beach Forest", "Stage Coach Hills", "Wattles Road area", "Long Lake Road corridor"],
  "auburn-hills": ["Oakland University area", "Featherstone Road corridor", "Adams Road", "Hawk Woods Park", "Squirrel Road area"],
  "birmingham": ["Quarton Lake", "Poppleton Park", "Pembroke Park", "Old Woodward", "Linden Park", "Maple-Adams corridor"],
  "pontiac": ["Oakland Avenue", "M-59 corridor", "Auburn Road area", "Telegraph corridor", "Crystal Lake area"],
  "royal-oak": ["Vinsetta Park", "Northwood", "Red Run", "Greenfield-Woodward area", "13 Mile-Coolidge corridor"],
  "lake-orion": ["Lake Orion Village", "Long Lake (Orion)", "Joslyn Road corridor", "Indianwood Lake", "Bald Eagle Lake"],
};

export function ServiceAreasHubPage() {
  return (
    <div>
      <SEO
        title="Service Areas in Oakland County, MI | Goral Construction"
        description="Roofing, siding & gutters across Oakland County - Waterford, Clarkston, White Lake, Commerce, West Bloomfield, Bloomfield Hills, Rochester Hills, Troy."
        path="/service-areas"
      />
      <section className="bg-bg-dark text-white py-16 md:py-28 relative overflow-hidden group">
        <DarkHeroAccent tone="skyline" />
        <div className="container-x relative">
          <div className="overline text-brand">Service areas</div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl md:text-7xl tracking-tight mt-3 max-w-3xl leading-[1.02]">Roofing across Oakland County, Michigan.</h1>
          <p className="text-white/80 text-base md:text-lg max-w-2xl mt-4 md:mt-5 leading-relaxed">Based in Waterford, we serve all major Oakland County communities. Same licensed crew, same craftsmanship, every zip code.</p>
          <div className="mt-6 md:mt-8"><CTA dark /></div>
        </div>
      </section>

      <section className="py-16">
        <div className="container-x aspect-[16/7] bg-bg-soft border border-ink-line overflow-hidden">
          <iframe title="Goral service area map" className="w-full h-full" src={`https://www.google.com/maps?q=${encodeURIComponent(BUSINESS.address.full)}&z=10&output=embed`} loading="lazy" />
        </div>
      </section>

      <section className="pb-16 md:pb-20">
        <div className="container-x">
          <HScroll className="md:grid-cols-2 lg:grid-cols-4 md:gap-px md:bg-ink-line md:border md:border-ink-line" testId="areas-hub-scroller">
            {CITIES.map((c, i) => {
              const accents = ["skyline", "window", "gable", "door", "vane", "shingles"];
              return (
                <HScrollItem key={c.slug} className="md:w-auto sm:w-[55%] w-[78%]">
                  <Link to={`/service-areas/${c.slug}`} className="group relative bg-white p-7 md:p-8 hover:bg-bg-soft h-full block border border-ink-line md:border-0 overflow-hidden transition-colors" data-testid={`areas-hub-${c.slug}`}>
                    <RoofAccent variant={accents[i % accents.length]} position="bottom-right" size="md" intensity="soft" />
                    <MapPin className="w-6 h-6 text-brand relative" />
                    <h3 className="font-heading text-xl md:text-2xl font-extrabold mt-4 relative">{c.name}, MI</h3>
                    <p className="text-ink-soft mt-2 text-sm leading-relaxed line-clamp-3 relative">{c.blurb}</p>
                    <div className="mt-5 text-brand font-semibold text-sm flex items-center gap-2 relative">Explore {c.name} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></div>
                  </Link>
                </HScrollItem>
              );
            })}
          </HScroll>
        </div>
      </section>
      <FinalCTA />
    </div>
  );
}

export function ServiceAreaDetailPage() {
  const { slug } = useParams();
  const [openFaq, setOpenFaq] = React.useState(null);
  const city = CITIES.find(c => c.slug === slug);
  if (!city) return <div className="container-x py-32"><h1 className="font-heading text-4xl font-bold">City not found</h1><Link to="/service-areas" className="btn-primary mt-6">All Areas</Link></div>;

  const cityReviews = REVIEWS.filter(r => r.city.toLowerCase().includes(city.name.toLowerCase().split(" ")[0]));
  const cityProjects = PROJECTS.filter(p => p.city.toLowerCase().includes(city.name.toLowerCase().split(" ")[0]));
  const intro = cityIntro[slug] || city.blurb;
  const faqs = cityFaqs(city);
  const neighborhoods = cityNeighborhoods[slug] || [];

  return (
    <div>
      <SEO
        title={`${city.name} Roofing Contractor | Roof Repair & Replacement MI | Goral`}
        description={`Expert roofing contractor in ${city.name}, MI. Roof repair, replacement, storm damage help & free inspections across ${neighborhoods[0] || city.name} and nearby. Call today!`}
        path={`/service-areas/${city.slug}`}
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            breadcrumbSchema([
              { name: "Home", path: "/" },
              { name: "Service Areas", path: "/service-areas" },
              { name: `${city.name}, MI`, path: `/service-areas/${city.slug}` },
            ]),
            faqSchema(faqs),
          ],
        }}
      />
      <section className="bg-bg-dark text-white py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0"><img src={IMAGES.snow} alt={city.name} className="w-full h-full object-cover opacity-35" /><div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-transparent" /></div>
        <div className="container-x relative">
          <Link to="/service-areas" className="overline text-brand hover:text-white">← All Service Areas</Link>
          <h1 className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight mt-4 max-w-3xl leading-[1.02]">Roofing Contractor in {city.name}, MI</h1>
          <p className="text-white/85 text-xl max-w-2xl mt-5 leading-relaxed">{city.blurb}</p>
          <div className="mt-8"><CTA dark /></div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="container-x grid lg:grid-cols-5 gap-14">
          <div className="lg:col-span-3">
            <SectionHeading overline={`Local expertise`} title={`Why ${city.name} homeowners choose Goral`} />
            <p className="mt-5 text-ink-soft leading-relaxed">{intro}</p>
            <p className="mt-4 text-ink-soft leading-relaxed">We're based 20 minutes from {city.name} in Waterford Township, which means faster response times on emergency calls and a crew that actually lives in the communities we work in.</p>
            <div className="mt-6 p-5 bg-bg-soft border-l-4 border-brand">
              <div className="overline">{city.name} Specifics</div>
              <p className="mt-2 font-medium">{city.highlight}</p>
            </div>

            {neighborhoods.length > 0 && (
              <div className="mt-8">
                <h3 className="font-heading text-lg font-bold">Neighborhoods we cover in {city.name}</h3>
                <p className="text-sm text-ink-soft mt-2 leading-relaxed">We regularly install roofs, siding and gutters across {neighborhoods.slice(0, -1).join(", ")} and {neighborhoods[neighborhoods.length - 1]}. Same crew, same warranty - whether you're a block off Main Street or out on a private lake road.</p>
              </div>
            )}

            <div className="mt-8 space-y-4">
              {[
                `Licensed & insured for work in ${city.name} and all Oakland County municipalities`,
                `Experienced with local HOA and permit requirements`,
                `Same-day response for emergency calls`,
                `Free, written estimates within 24-48 hours`,
              ].map(x => <div key={x} className="flex gap-3 items-start"><CheckCircle2 className="w-5 h-5 text-brand mt-0.5" /><span>{x}</span></div>)}
            </div>
          </div>
          <aside className="lg:col-span-2">
            <div className="border border-ink-line bg-white p-6 sticky top-24">
              <div className="overline">{city.name}, MI</div>
              <div className="font-heading text-2xl font-extrabold mt-2">Free roof inspection in {city.name}</div>
              <p className="text-sm text-ink-soft mt-2">Typically responding within 24 hours. We&apos;ll walk every slope, photograph the deck, and email you a written quote within 48 hours.</p>
              <Link to="/get-estimate" className="btn-primary w-full justify-center mt-5">Book {city.name} inspection <ArrowRight className="w-4 h-4" /></Link>
              <a href={`tel:${BUSINESS.phoneTel}`} className="btn-secondary w-full justify-center mt-2">Call {BUSINESS.phone}</a>
            </div>
          </aside>
        </div>
      </section>

      <section className="py-16 bg-bg-soft">
        <div className="container-x">
          <SectionHeading overline="Services in the area" title={`What we install & repair in ${city.name}`} />
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-ink-line border border-ink-line">
            {SERVICES.map(s => (
              <Link to={`/services/${s.slug}`} key={s.slug} className="bg-white p-5 hover:bg-bg-soft group">
                <div className="font-heading text-lg font-bold">{s.name}</div>
                <div className="text-sm text-ink-soft mt-1">{s.short}</div>
                <div className="mt-3 text-brand text-sm font-semibold flex items-center gap-1">Learn more <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {cityProjects.length > 0 && (
        <section className="py-20">
          <div className="container-x">
            <SectionHeading overline="Recent work" title={`Projects in ${city.name}`} />
            <div className="mt-10 grid md:grid-cols-3 gap-6">
              {cityProjects.map(p => (
                <div key={p.id}><div className="aspect-[4/3] overflow-hidden"><img src={p.image} alt={p.type} className="w-full h-full object-cover" /></div><div className="mt-4"><div className="overline">{p.date}</div><div className="font-heading text-xl font-bold mt-1">{p.type}</div><div className="text-ink-soft text-sm mt-1">{p.material}</div></div></div>
              ))}
            </div>
          </div>
        </section>
      )}

      {cityReviews.length > 0 && (
        <section className="py-20 bg-bg-soft">
          <div className="container-x">
            <SectionHeading overline="What neighbors say" title={`Reviews from ${city.name}`} />
            <div className="mt-10 grid md:grid-cols-3 gap-6">{cityReviews.map(r => <ReviewCard key={r.name} r={r} />)}</div>
          </div>
        </section>
      )}

      {/* City-specific FAQ section (unique per city) */}
      <section className="py-20">
        <div className="container-x grid md:grid-cols-3 gap-10">
          <div><SectionHeading overline="FAQs" title={`Roofing questions from ${city.name} homeowners`} /></div>
          <div className="md:col-span-2">
            {faqs.map((f, i) => (
              <div key={f.q} className="border-b border-ink-line">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full text-left py-5 flex justify-between items-start gap-4">
                  <span className="font-heading text-lg font-bold">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 flex-shrink-0 mt-1 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <div className="pb-5 text-ink-soft leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services x City - internal linking grid that ends "orphaned pages" Semrush warning.
          Every combo page (/services/X/in/Y) is now reachable from at least one city page. */}
      <section className="py-16 md:py-20 bg-bg-soft border-t border-ink-line">
        <div className="container-x">
          <SectionHeading
            overline={`${city.name} services`}
            title={`Roofing, siding & gutter services in ${city.name}, MI`}
            sub={`Each link below opens a page focused on that specific service for ${city.name} homeowners - process, materials, pricing and ${city.name}-specific notes.`}
          />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { slug: "roof-replacement",   label: "Roof Replacement" },
              { slug: "roof-repair",        label: "Roof Repair" },
              { slug: "storm-damage",       label: "Storm Damage Restoration" },
              { slug: "insurance-claims",   label: "Insurance Claim Help" },
              { slug: "residential-roofing",label: "Residential Roofing" },
              { slug: "siding",             label: "Siding Installation" },
              { slug: "gutters",            label: "Seamless Gutters" },
            ].map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}/in/${city.slug}`}
                className="group flex items-center justify-between gap-3 px-5 py-4 bg-white border border-ink-line hover:border-brand transition-colors"
                data-testid={`city-service-link-${s.slug}`}
              >
                <div>
                  <div className="overline text-ink-soft group-hover:text-brand transition-colors">In {city.name}</div>
                  <div className="font-heading text-base font-bold mt-0.5 group-hover:text-brand transition-colors">{s.label}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-ink-soft group-hover:text-brand group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Specialty repairs cross-link — every deep-service page gets a
          contextual internal link from every city, fixing Semrush's
          "orphaned pages" warning without creating new URLs. */}
      <section className="py-16 md:py-20 border-t border-ink-line">
        <div className="container-x">
          <SectionHeading
            overline="Specialty roof work"
            title={`Specific repairs Goral handles in ${city.name}`}
            sub={`When a full replacement isn't what you need. Each page below walks through when it applies, what it costs in Michigan, and how insurance treats it.`}
          />
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {DEEP_SERVICES.map((d) => (
              <Link
                key={d.slug}
                to={`/services/${d.slug}`}
                className="group flex flex-col justify-between p-4 bg-white border border-ink-line hover:border-brand transition-colors text-sm"
                data-testid={`city-deep-service-${d.slug}`}
              >
                <div className="font-heading font-bold leading-snug group-hover:text-brand transition-colors">{d.name}</div>
                <ArrowRight className="w-3.5 h-3.5 text-ink-soft group-hover:text-brand group-hover:translate-x-1 transition-all mt-3" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
